/**
 * 1.3.82: プライバシーポリシーページ
 * 改定 2026-05-13:
 *  - 第一部「YUIChat（Web 版）」は原文をそのまま維持
 *  - 第二部「LINE 公式アカウント『AIぼう』」を追加（個情法 2022 改正・改正電気通信事業法・LINE 認証審査準拠）
 *  - 両者は別建てで明示的に区分。共通する事項は各章で独立に記述
 */

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="戻る"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">プライバシーポリシー</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        {/* ────────────── 目次 / 案内 ────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <p className="text-sm text-gray-600 leading-relaxed">
            本ページでは、当方が提供する以下のサービスのプライバシーポリシーをそれぞれ別建てで掲載しております。
          </p>
          <ul className="mt-3 space-y-1 text-sm">
            <li>
              <a href="#yuichat" className="text-primary hover:underline">
                第一部：YUIChat（Web 版）プライバシーポリシー
              </a>
            </li>
            <li>
              <a href="#aibou" className="text-primary hover:underline">
                第二部：LINE 公式アカウント「AIぼう」プライバシーポリシー
              </a>
            </li>
          </ul>
        </div>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* 第一部：YUIChat（原文そのまま） */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div id="yuichat" className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-primary to-purple-600 p-8 text-white">
            <p className="text-xs text-purple-100 uppercase tracking-wide mb-1">第一部 / Part 1</p>
            <h2 className="text-2xl font-bold mb-2">YUIChat プライバシーポリシー</h2>
            <p className="text-purple-100 text-sm">Privacy Policy（Web 版）</p>
          </div>

          {/* Policy Content */}
          <div className="p-8 space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-gray-700 leading-relaxed">
                YUIChat（以下「当サービス」）は、お客様の個人情報の保護を重要視しております。
                本プライバシーポリシーは、当サービスがお客様から収集する情報、その利用方法、
                および保護について説明するものです。
              </p>
            </section>

            {/* Section 1 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-sm font-bold">1</span>
                収集する情報
              </h3>
              <div className="pl-10 space-y-3 text-gray-700">
                <p>当サービスは、以下の情報を収集することがあります：</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>アカウント情報（メールアドレス、氏名など）</li>
                  <li>サービス利用に関する情報（利用履歴、対話履歴など）</li>
                  <li>お問い合わせ内容</li>
                  <li>Cookie およびアクセスログ情報</li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-sm font-bold">2</span>
                情報の利用目的
              </h3>
              <div className="pl-10 space-y-3 text-gray-700">
                <p>収集した情報は、以下の目的で利用いたします：</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>サービスの提供および運営</li>
                  <li>お客様サポートの提供</li>
                  <li>サービスの改善および新機能の開発</li>
                  <li>利用状況の分析</li>
                  <li>重要なお知らせの送信</li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-sm font-bold">3</span>
                情報の第三者提供
              </h3>
              <div className="pl-10 text-gray-700">
                <p>
                  当サービスは、以下の場合を除き、お客様の個人情報を第三者に提供することはありません：
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
                  <li>お客様の同意がある場合</li>
                  <li>法令に基づく場合</li>
                  <li>サービス提供に必要な業務委託先への提供（適切な管理下において）</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-sm font-bold">4</span>
                情報の安全管理
              </h3>
              <div className="pl-10 text-gray-700">
                <p>
                  当サービスは、お客様の個人情報を適切に管理し、不正アクセス、紛失、破壊、
                  改ざん、漏洩などを防止するため、必要かつ適切なセキュリティ対策を実施しております。
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-sm font-bold">5</span>
                Cookie の使用
              </h3>
              <div className="pl-10 text-gray-700">
                <p>
                  当サービスは、サービスの利便性向上のため Cookie を使用しております。
                  お客様はブラウザの設定により Cookie を無効にすることができますが、
                  一部のサービス機能が制限される場合があります。
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-sm font-bold">6</span>
                お客様の権利
              </h3>
              <div className="pl-10 text-gray-700">
                <p>お客様は、以下の権利を有しております：</p>
                <ul className="list-disc list-inside space-y-2 ml-4 mt-3">
                  <li>個人情報の開示請求</li>
                  <li>個人情報の訂正・削除請求</li>
                  <li>個人情報の利用停止請求</li>
                </ul>
                <p className="mt-3">
                  これらのご請求は、下記のお問い合わせ先までご連絡ください。
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-sm font-bold">7</span>
                ポリシーの変更
              </h3>
              <div className="pl-10 text-gray-700">
                <p>
                  当サービスは、必要に応じて本プライバシーポリシーを変更することがあります。
                  重要な変更がある場合は、サービス上でお知らせいたします。
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-primary/10 text-primary rounded-lg flex items-center justify-center text-sm font-bold">8</span>
                お問い合わせ
              </h3>
              <div className="pl-10 text-gray-700">
                <p>
                  本プライバシーポリシーに関するお問い合わせは、下記までご連絡ください。
                </p>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium">YUIChat 運営</p>
                  <p className="mt-2">
                    メール：
                    <a href="mailto:contact@yuichat.app" className="text-primary hover:text-primary-dark">
                      contact@yuichat.app
                    </a>
                  </p>
                </div>
              </div>
            </section>

            {/* Last Updated */}
            <div className="pt-8 border-t border-gray-100 text-center text-sm text-gray-400">
              制定日：2026年2月1日
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* 第二部：LINE 公式アカウント「AIぼう」 */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div id="aibou" className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Title Section */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white">
            <p className="text-xs text-green-100 uppercase tracking-wide mb-1">第二部 / Part 2</p>
            <h2 className="text-2xl font-bold mb-2">LINE 公式アカウント「AIぼう」プライバシーポリシー</h2>
            <p className="text-green-100 text-sm">Privacy Policy（LINE Official Account）</p>
          </div>

          <div className="p-8 space-y-8">
            {/* Intro */}
            <section>
              <p className="text-gray-700 leading-relaxed">
                YUIChat 運営（以下「当方」といいます）は、当方が運営する LINE 公式アカウント「AIぼう」（以下「本 LINE アカウント」といいます）をご利用になる皆様（以下「ユーザー」といいます）の情報を以下のように取り扱うものとします。本プライバシーポリシーは、第一部「YUIChat（Web 版）」とは別建てで、LINE 公式アカウント特有の取扱いを定めるものです。
              </p>
            </section>

            {/* 第1条 関係法令等の遵守 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第1条</span>
                関係法令、ガイドライン等の遵守
              </h3>
              <div className="pl-4 text-gray-700">
                <p>
                  当方は、個人情報の保護に関する法律（以下「個人情報保護法」といいます）、電気通信事業法、LINE Official Account Guideline その他の個人情報保護に関する法令およびガイドライン等を遵守いたします。
                </p>
              </div>
            </section>

            {/* 第2条 取得する情報 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第2条</span>
                取得する情報
              </h3>
              <div className="pl-4 space-y-3 text-gray-700">
                <p>当方は、本 LINE アカウントの提供にあたり以下の情報を取得する可能性があります。</p>
                <ol className="list-decimal list-inside space-y-1 ml-4 text-sm">
                  <li>LINE ユーザー識別子（LINE ID。LINE ヤフー株式会社が発行する識別子）</li>
                  <li>LINE 表示名およびプロフィール画像（ユーザーがご提供を許可された場合のみ）</li>
                  <li>ユーザーが本 LINE アカウントに送信したメッセージ内容（テキスト、画像、音声、スタンプ、ファイル等）</li>
                  <li>本 LINE アカウントからの応答内容および送受信のタイムスタンプ</li>
                  <li>LINE グループ ID（グループチャットでご利用の場合）およびグループ内のメッセージ送受信情報</li>
                  <li>ユーザーが会話中に自発的に開示された属性情報（氏名、所在地域、職業、家族構成、趣味嗜好等）</li>
                </ol>
                <p className="text-sm bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                  ⚠️ 当方は、要配慮個人情報（健康状態、病歴、信条等。個人情報保護法第 2 条第 3 項）を積極的に取得しません。ただし、ユーザーが本 LINE アカウント上で自発的に要配慮個人情報を送信した場合には、ユーザーは当方が当該情報を取得することに同意したものとみなします。
                </p>
              </div>
            </section>

            {/* 第3条 利用目的 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第3条</span>
                利用目的
              </h3>
              <div className="pl-4 space-y-2 text-gray-700">
                <p>取得した情報は、以下の目的の範囲内でのみ利用いたします（個人情報保護法第 17 条）。</p>
                <ol className="list-decimal list-inside space-y-1 ml-4 text-sm">
                  <li>本 LINE アカウントを通じた AI 自動応答サービスの提供</li>
                  <li>ユーザーからのお問い合わせ対応</li>
                  <li>本 LINE アカウントの不具合調査・改善および新機能の開発</li>
                  <li>利用状況の統計的分析（個人を特定できない形式での集計）</li>
                  <li>不正利用・違法行為の防止および対応</li>
                  <li>サービス変更・終了等の重要なお知らせの送信</li>
                </ol>
              </div>
            </section>

            {/* 第4条 AI による自動応答 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第4条</span>
                AI による自動応答について
              </h3>
              <div className="pl-4 space-y-3 text-gray-700">
                <p>「AIぼう」は AI（人工知能）による自動応答サービスです。ご利用に際しては以下の点にご留意ください。</p>
                <ul className="list-disc list-inside space-y-2 ml-4 text-sm">
                  <li>返信内容は <strong>AI によって自動生成されるもの</strong>であり、人間による応答ではありません。</li>
                  <li>AI の応答内容は<strong>必ずしも正確性を保証するものではなく</strong>、最新性・適法性・適合性についても保証いたしません。</li>
                  <li>医療・法律・投資・税務など<strong>専門的判断を要する事項</strong>については、必ず該当分野の有資格専門家にご相談ください。AI 応答を根拠とした行動の結果について、当方は一切の責任を負いません。</li>
                  <li>グループチャットでご利用の場合、グループ内の他のメンバーの発言も AI 応答生成のコンテキストとして処理されます。</li>
                </ul>
                <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500 text-sm">
                  <p className="font-bold mb-2">🆘 緊急時のご案内</p>
                  <p>ユーザーの生命・身体・財産に関わる緊急時は、AI ではなく適切な専門機関にご連絡ください。</p>
                  <ul className="mt-2 space-y-1">
                    <li>・救急医療：<strong>119</strong>（消防）</li>
                    <li>・事件・事故：<strong>110</strong>（警察）</li>
                    <li>・自殺・自傷の悩み：<strong>いのちの電話 0570-783-556</strong>（10:00-22:00）</li>
                    <li>・よりそいホットライン：<strong>0120-279-338</strong>（24 時間）</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 第5条 外部送信・情報収集モジュール */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第5条</span>
                外部送信・情報収集モジュールの利用
              </h3>
              <div className="pl-4 space-y-4 text-gray-700">
                <p className="text-sm">
                  当方は、本 LINE アカウントの運営および AI 応答生成のため、本 LINE アカウントの機能とは独立した第三者の API・モジュールを利用することがあります。各事業者の名称、送信される情報の内容、利用目的は以下のとおりです（改正電気通信事業法 外部送信規律準拠）。
                </p>

                {/* LINE */}
                <div className="border-l-2 border-green-400 pl-4 py-1">
                  <h4 className="font-bold text-gray-900">
                    <a href="https://terms2.line.me/line_live_privacy?lang=ja" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      LINE ヤフー株式会社（日本）
                    </a>
                  </h4>
                  <ul className="text-sm space-y-1 mt-2">
                    <li>・送信される項目：LINE ユーザー識別子、LINE 表示名・プロフィール画像、メッセージ内容</li>
                    <li>・利用目的：本 LINE アカウントのメッセージ送受信基盤の提供</li>
                    <li>・送信される仕組み：LINE Messaging API を通じた連携</li>
                  </ul>
                </div>

                {/* Supabase */}
                <div className="border-l-2 border-green-400 pl-4 py-1">
                  <h4 className="font-bold text-gray-900">
                    <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Supabase Inc.(米国 / シンガポール)
                    </a>
                  </h4>
                  <ul className="text-sm space-y-1 mt-2">
                    <li>・送信される項目：LINE ユーザー識別子、対話履歴、アップロードファイル</li>
                    <li>・利用目的：対話データの保存および認証基盤の提供</li>
                    <li>・送信される仕組み：Supabase クライアント SDK / API を通じた連携</li>
                  </ul>
                </div>

                {/* Stripe */}
                <div className="border-l-2 border-green-400 pl-4 py-1">
                  <h4 className="font-bold text-gray-900">
                    <a href="https://stripe.com/jp/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Stripe, Inc.（米国）
                    </a>
                  </h4>
                  <ul className="text-sm space-y-1 mt-2">
                    <li>・送信される項目：購入内容、決済金額、決済日時、取引 ID、クレジットカード下 4 桁、請求先情報</li>
                    <li>・利用目的：有料機能・サブスクリプション等の決済処理のため</li>
                    <li>・送信される仕組み：Stripe の決済 API を通じた連携</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 第6条 外国にある第三者への提供 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第6条</span>
                外国にある第三者への提供について
              </h3>
              <div className="pl-4 space-y-3 text-gray-700">
                <p>
                  第 5 条に記載のとおり、当方は本 LINE アカウントの運営にあたり、ユーザーの情報の一部を米国およびシンガポールに所在する事業者に委託しております（個人情報保護法第 28 条）。
                </p>
                <p className="text-sm">
                  <strong>米国</strong>は、個人情報保護委員会が定める「個人の権利利益を保護する上で我が国と同等の水準にあると認められる個人情報の保護に関する制度を有している外国」には<strong>該当しません</strong>。当該国における個人情報保護に関する制度の詳細は、個人情報保護委員会のウェブサイト（
                  <a href="https://www.ppc.go.jp/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    https://www.ppc.go.jp/
                  </a>
                  ）をご参照ください。<strong>シンガポール</strong>は、個人情報保護委員会が指定する「我が国と同等の水準国」に該当します（2019 年 1 月相互認定）。
                </p>
                <p className="text-sm">
                  当方は各委託先と適切なデータ処理契約を締結し、ユーザーの個人情報が委託先所在国の法令により本ポリシーに反する形で開示・利用されないよう必要な措置を講じております。
                </p>
              </div>
            </section>

            {/* 第7条 AI 学習データへの利用 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第7条</span>
                AI 学習データへの利用について
              </h3>
              <div className="pl-4 space-y-3 text-gray-700">
                <p className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <strong>ユーザーの対話内容は、AI モデルの学習データとして利用されません。</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                  <li>当方が利用する AI モデル提供事業者の API は、「入力データを学習に利用しない」契約条件下で利用しております（各社 API 利用規約に基づく）。</li>
                  <li>当方自身も、ユーザーの同意なくユーザーの対話内容を AI モデルの学習・ファインチューニング・プロンプト調整等に利用することはありません。</li>
                  <li>サービス品質向上のため、対話内容を匿名化・統計化した上で分析することがありますが、個人を特定できる形式での利用はいたしません。</li>
                </ul>
                <p className="text-sm">
                  将来的に学習目的での利用を行う場合は、事前に明示的な同意取得（オプトイン）の手続きを経た上で実施し、本ポリシーを改定の上お知らせいたします。
                </p>
                <p className="text-sm bg-gray-50 p-3 rounded-lg">
                  📝 当方が利用する AI モデル提供事業者の具体的な名称については、お問い合わせ窓口（第 11 条）までご連絡いただければご案内いたします。
                </p>
              </div>
            </section>

            {/* 第8条 安全管理措置 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第8条</span>
                安全管理措置
              </h3>
              <div className="pl-4 space-y-2 text-gray-700">
                <p>当方は、個人情報保護法第 23 条に基づき、以下の安全管理措置を講じております。</p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                  <li><strong>組織的安全管理</strong>：個人情報取扱責任者の設置、社内規程の整備</li>
                  <li><strong>人的安全管理</strong>：従業者および業務委託先への教育、機密保持契約の締結</li>
                  <li><strong>技術的安全管理</strong>：通信の TLS 暗号化、データベースの暗号化保存、アクセス権限の最小化、操作ログの監査</li>
                  <li><strong>物理的安全管理</strong>：データセンターへの入退室制限</li>
                </ul>
              </div>
            </section>

            {/* 第9条 保存期間 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第9条</span>
                個人情報の保存期間
              </h3>
              <div className="pl-4 space-y-2 text-gray-700">
                <p>当方は、以下の期間ユーザーの情報を保存いたします。</p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                  <li><strong>LINE ユーザー識別子・プロフィール情報</strong>：本 LINE アカウントをブロック解除いただいてから 90 日間</li>
                  <li><strong>対話履歴</strong>：原則として直近 12 ヶ月分。それ以前のデータは順次匿名化または削除いたします</li>
                  <li><strong>アクセスログ・操作ログ</strong>：6 ヶ月</li>
                  <li><strong>お問い合わせ履歴</strong>：対応完了後 3 年間</li>
                </ul>
                <p className="text-sm">
                  ユーザーが本 LINE アカウントをブロックされた場合、当方からのメッセージ送信は停止されますが、保存期間中はデータが保持されます。完全削除をご希望の場合は第 11 条の窓口までご連絡ください。
                </p>
              </div>
            </section>

            {/* 第10条 未成年者のご利用 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第10条</span>
                未成年者のご利用について
              </h3>
              <div className="pl-4 space-y-2 text-gray-700">
                <p>
                  <strong>18 歳未満</strong>のユーザーは、必ず保護者の同意のもとで本 LINE アカウントをご利用ください。当方は、未成年者のユーザーからメッセージを受信した際、保護者の方が本ポリシーの内容をご確認・ご同意いただいたものとみなします。
                </p>
                <p className="text-sm">
                  保護者の方は、お子様の LINE アカウントに紐づく情報について、開示・削除等を当方に請求することができます。
                </p>
              </div>
            </section>

            {/* 第11条 ユーザーの権利およびお問い合わせ窓口 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第11条</span>
                ユーザーの権利およびお問い合わせ窓口
              </h3>
              <div className="pl-4 space-y-4 text-gray-700">
                <p className="text-sm">
                  ユーザーは、ご自身の個人情報について以下の権利を有しております（個人情報保護法第 28 条～第 35 条）。
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                  <li>保有個人データの<strong>開示</strong>請求</li>
                  <li>個人情報の<strong>訂正・追加・削除</strong>請求</li>
                  <li>個人情報の<strong>利用停止・消去</strong>請求</li>
                  <li>第三者への<strong>提供停止</strong>請求</li>
                </ul>
                <p className="text-sm bg-blue-50 p-3 rounded-lg">
                  📩 これらのご請求は、本人確認の上、以下のお問い合わせ先までご連絡ください。<strong>原則として請求受領から 30 日以内に対応</strong>いたします。
                </p>
                <div className="p-4 bg-gray-50 rounded-lg space-y-1 text-sm">
                  <p><strong>事業者名</strong>：YUIChat 運営</p>
                  <p><strong>個人情報取扱責任者</strong>：YUIChat 個人情報担当者</p>
                  <p>
                    <strong>連絡先</strong>：
                    <a href="mailto:contact@yuichat.app" className="text-primary hover:underline">
                      contact@yuichat.app
                    </a>
                  </p>
                  <p><strong>受付時間</strong>：平日 10:00 - 18:00（土日祝・年末年始を除く）</p>
                </div>
                <div className="text-sm">
                  <p className="font-medium text-gray-900 mb-1">【個人情報保護に関する公的苦情窓口】</p>
                  <p>
                    本ポリシーに関するお問い合わせとは別に、個人情報の取扱いについて苦情の申出ができます。<br />
                    <strong>個人情報保護委員会</strong>：
                    <a href="https://www.ppc.go.jp/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      https://www.ppc.go.jp/
                    </a>
                    （個人情報保護法相談ダイヤル：03-6457-9849）
                  </p>
                </div>
              </div>
            </section>

            {/* 第12条 改定 */}
            <section>
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-bold">第12条</span>
                本ポリシーの改定
              </h3>
              <div className="pl-4 text-gray-700 space-y-2">
                <p>
                  当方は、法令の改正、事業内容の変更、ユーザーの利便性向上のため、必要に応じて本プライバシーポリシーを改定することがあります。重要な変更がある場合は、本 LINE アカウントを通じてお知らせいたします。
                </p>
              </div>
            </section>

            {/* 制定日 */}
            <div className="pt-8 border-t border-gray-100 text-center text-sm text-gray-400">
              <p>制定日：2026 年 5 月 13 日</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <button
            onClick={() => {
              // 1.3.84: 使用 sessionStorage 传递滚动意图，更可靠
              sessionStorage.setItem('scrollToFooter', 'true');
              window.location.href = '/';
            }}
            className="px-6 py-3 text-primary hover:text-primary-dark font-medium transition-colors"
          >
            ← トップページに戻る
          </button>
        </div>
      </main>
    </div>
  );
};
